import React from 'react';
import type { WxbStatusTone } from '../StatusBadge/StatusBadge';
import {
  addCalendarMonths,
  buildWxbCalendarMatrix,
  getCalendarMonthTitle,
  parseCalendarDateKey,
  toCalendarDateKey,
} from './calendarUtils';
import type { WxbCalendarDateCell, WxbCalendarWeekStart } from './calendarUtils';
import './Calendar.css';

export interface WxbCalendarEvent {
  key: string;
  date: string;
  title: React.ReactNode;
  tone?: WxbStatusTone;
  meta?: React.ReactNode;
}

export interface WxbCalendarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  value?: string;
  defaultValue?: string;
  viewDate?: string;
  defaultViewDate?: string;
  events?: WxbCalendarEvent[];
  minDate?: string;
  maxDate?: string;
  disabledDate?: (date: Date) => boolean;
  onSelect?: (dateKey: string, cell: WxbCalendarDateCell) => void;
  onMonthChange?: (monthKey: string) => void;
  weekStartsOn?: WxbCalendarWeekStart;
  density?: 'comfortable' | 'compact';
  maxEventRows?: number;
  showOutsideDays?: boolean;
}

const WEEKDAY_LABELS: Record<WxbCalendarWeekStart, string[]> = {
  0: ['日', '一', '二', '三', '四', '五', '六'],
  1: ['一', '二', '三', '四', '五', '六', '日'],
};

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

function buildEventMap(events: WxbCalendarEvent[]) {
  return events.reduce<Record<string, WxbCalendarEvent[]>>((acc, event) => {
    if (!acc[event.date]) acc[event.date] = [];
    acc[event.date].push(event);
    return acc;
  }, {});
}

export const WxbCalendar: React.FC<WxbCalendarProps> = ({
  value,
  defaultValue,
  viewDate,
  defaultViewDate,
  events = [],
  minDate,
  maxDate,
  disabledDate,
  onSelect,
  onMonthChange,
  weekStartsOn = 1,
  density = 'comfortable',
  maxEventRows = 2,
  showOutsideDays = true,
  className = '',
  ...props
}) => {
  const initialSelectedDate = React.useMemo(
    () => parseCalendarDateKey(defaultValue) ?? parseCalendarDateKey(value),
    [defaultValue, value],
  );
  const [internalSelectedDate, setInternalSelectedDate] = React.useState<Date | null>(initialSelectedDate);
  const selectedDate = parseCalendarDateKey(value) ?? internalSelectedDate;

  const initialViewDate = React.useMemo(
    () => (
      parseCalendarDateKey(defaultViewDate)
      ?? parseCalendarDateKey(viewDate)
      ?? selectedDate
      ?? new Date()
    ),
    [defaultViewDate, selectedDate, viewDate],
  );
  const [internalViewDate, setInternalViewDate] = React.useState<Date>(initialViewDate);
  const resolvedViewDate = parseCalendarDateKey(viewDate) ?? internalViewDate;
  const minDateValue = parseCalendarDateKey(minDate);
  const maxDateValue = parseCalendarDateKey(maxDate);
  const eventMap = React.useMemo(() => buildEventMap(events), [events]);

  const cells = React.useMemo(() => buildWxbCalendarMatrix({
    viewDate: resolvedViewDate,
    selectedDate,
    weekStartsOn,
    minDate: minDateValue,
    maxDate: maxDateValue,
    disabledDate,
  }), [disabledDate, maxDateValue, minDateValue, resolvedViewDate, selectedDate, weekStartsOn]);

  const updateViewDate = (nextViewDate: Date) => {
    if (viewDate === undefined) setInternalViewDate(nextViewDate);
    onMonthChange?.(toCalendarDateKey(nextViewDate).slice(0, 7));
  };

  const selectCell = (cell: WxbCalendarDateCell) => {
    if (cell.isDisabled) return;

    if (value === undefined) setInternalSelectedDate(cell.date);
    if (!cell.isCurrentMonth) updateViewDate(cell.date);
    onSelect?.(cell.key, cell);
  };

  const goToday = () => {
    const today = new Date();
    const todayCell = buildWxbCalendarMatrix({
      viewDate: today,
      selectedDate: today,
      weekStartsOn,
      minDate: minDateValue,
      maxDate: maxDateValue,
      disabledDate,
    }).find((cell) => cell.key === toCalendarDateKey(today));

    updateViewDate(today);
    if (!todayCell || todayCell.isDisabled) return;
    if (value === undefined) setInternalSelectedDate(todayCell.date);
    onSelect?.(todayCell.key, todayCell);
  };

  return (
    <div className={joinClasses('wxb-calendar', `wxb-calendar-${density}`, className)} {...props}>
      <div className="wxb-calendar-header">
        <div className="wxb-calendar-title-group">
          <div className="wxb-calendar-eyebrow">Calendar</div>
          <h3 className="wxb-calendar-title">{getCalendarMonthTitle(resolvedViewDate)}</h3>
        </div>
        <div className="wxb-calendar-actions" aria-label="日历月份切换">
          <button
            type="button"
            className="wxb-calendar-nav-button"
            aria-label="上个月"
            onClick={() => updateViewDate(addCalendarMonths(resolvedViewDate, -1))}
          >
            <span aria-hidden="true">‹</span>
          </button>
          <button type="button" className="wxb-calendar-today-button" onClick={goToday}>
            今天
          </button>
          <button
            type="button"
            className="wxb-calendar-nav-button"
            aria-label="下个月"
            onClick={() => updateViewDate(addCalendarMonths(resolvedViewDate, 1))}
          >
            <span aria-hidden="true">›</span>
          </button>
        </div>
      </div>

      <div className="wxb-calendar-grid" role="grid" aria-label={getCalendarMonthTitle(resolvedViewDate)}>
        {WEEKDAY_LABELS[weekStartsOn].map((weekday) => (
          <div key={weekday} className="wxb-calendar-weekday-head" role="columnheader">
            {weekday}
          </div>
        ))}
        {cells.map((cell) => {
          const dayEvents = eventMap[cell.key] ?? [];
          const visibleEvents = dayEvents.slice(0, maxEventRows);
          const overflowCount = dayEvents.length - visibleEvents.length;

          return (
            <button
              key={cell.key}
              type="button"
              role="gridcell"
              aria-selected={cell.isSelected}
              aria-current={cell.isToday ? 'date' : undefined}
              disabled={cell.isDisabled}
              className={joinClasses(
                'wxb-calendar-cell',
                cell.isCurrentMonth && 'is-current-month',
                !cell.isCurrentMonth && 'is-outside-month',
                !showOutsideDays && !cell.isCurrentMonth && 'is-hidden-outside',
                cell.isToday && 'is-today',
                cell.isSelected && 'is-selected',
                cell.isDisabled && 'is-disabled',
              )}
              onClick={() => selectCell(cell)}
            >
              <span className="wxb-calendar-cell-top">
                <span className="wxb-calendar-cell-date">{cell.dayOfMonth}</span>
                {cell.isToday && <span className="wxb-calendar-today-dot" aria-hidden="true" />}
              </span>
              {visibleEvents.length > 0 && (
                <span className="wxb-calendar-event-list">
                  {visibleEvents.map((event) => (
                    <span
                      key={event.key}
                      className={joinClasses('wxb-calendar-event-pill', `wxb-calendar-event-pill-${event.tone ?? 'neutral'}`)}
                    >
                      <span className="wxb-calendar-event-title">{event.title}</span>
                      {event.meta && <span className="wxb-calendar-event-meta">{event.meta}</span>}
                    </span>
                  ))}
                  {overflowCount > 0 && (
                    <span className="wxb-calendar-more">+{overflowCount}</span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
